<div class="ntl">
  @if (error) {
    <div class="ntl__state ntl__state--error">{{ error }}</div>
  }

  @if (loading && !error) {
    <div class="ntl__state">
      <span class="ntl__spinner" aria-hidden="true"></span>
      Loading notifications…
    </div>
  }

  @if (!loading && !error && notifications.length === 0) {
    <div class="ntl__state ntl__state--empty">
      <span class="ntl__empty-mark" aria-hidden="true">✓</span>
      <p class="ntl__empty-title">You're all caught up</p>
      <p class="ntl__empty-sub">New notifications will appear here.</p>
    </div>
  }

  @if (!loading && !error && notifications.length > 0) {
    <!-- Grouped by day so the list reads as a timeline rather than a wall. -->
    @for (group of groups; track group.label) {
      <div class="ntl__dayhead">
        <p class="ntl__daysep">{{ group.label }}</p>
        <!--
          Clearing by day is the granularity people actually think in. Mark-all
          is all-or-nothing and per-item is one click each; this sits between
          them, and goes over one bulk request rather than one per row.
        -->
        <button
          type="button"
          class="ntl__dayclear"
          (click)="onClearGroup(group, $event)"
          [attr.aria-label]="'Clear ' + group.items.length + ' notifications from ' + group.label"
          >
          Clear
        </button>
      </div>
      <ul class="ntl__items">
        @for (entry of group.entries; track entry.key) {
          <!--
            Hover and unread live on the LI, not on the row button.

            The row button is no longer the whole card: the Open control is a
            sibling below it. With the fill on the row, hovering highlighted
            everything except that strip and the card looked half-lit. The li
            is the only element that spans the whole notification.
          -->
          <li
            [class.ntl__item]="entry.kind === 'row'"
            [class.ntl__item--unread]="entry.kind === 'row' && entry.row.unread"
            >
            @if (entry.kind === 'stack') {
              <!--
                A run of same-type notifications standing in for its members.
                Exports arrive a dozen at a time from one batch job; eleven
                near-identical rows push the rest of the day off the screen.
              -->
              <button
                type="button"
                class="ntl__stack"
                (click)="toggleStack(entry.stack, $event)"
                [attr.aria-expanded]="entry.stack.expanded"
                >
                <span class="ntl__stack-text">
                  <strong>{{ entry.stack.label }}</strong>
                  <span class="ntl__stack-time">· last {{ entry.stack.relativeTime }}</span>
                </span>
                <span class="ntl__stack-toggle">{{ entry.stack.expanded ? 'Hide' : 'Show' }}</span>
              </button>
            } @else {
              <!--
                READING AND GOING ARE TWO DIFFERENT ACTIONS.
                The row marks read and stays put; only the Open link navigates.

                The row used to navigate, which made clearing a backlog cost one
                round trip through a record per notification — the panel closed
                every single time. With a hundred notifications that is a
                hundred page loads to reach an empty list, and it is why the
                per-row tick existed at all. The tick is gone: the row itself now
                does what it did, over the whole target instead of a 20px icon.

                The controls beside the row are siblings, not children. A
                <button> nested inside a <button> is invalid HTML and browsers
                resolve it by silently dropping one.
              -->
              <span class="ntl__actions">
                <button
                  type="button"
                  class="ntl__act"
                  (click)="onDismiss(entry.row.notification, $event)"
                  [attr.aria-label]="'Dismiss: ' + entry.row.plainTitle"
                  title="Dismiss"
                  >
                  <mat-icon class="ntl__act-icon" svgIcon="c:x"></mat-icon>
                </button>
              </span>
              <button
                type="button"
                class="ntl__row"
                [class.ntl__row--unread]="entry.row.unread"
                [class.ntl__row--linkable]="entry.row.linkable"
                [class.ntl__row--stacked]="entry.stacked"
                (click)="onMarkRead(entry.row.notification, $event)"
                [attr.aria-label]="(entry.row.unread ? 'Mark as read: ' : '') + entry.row.presentation.label + ': ' + entry.row.plainTitle"
                >
                <span
                  [class]="'ntl__mark ntl__mark--' + entry.row.presentation.tint"
                  aria-hidden="true"
                  >
                  @if ((entry.row.presentation.icon) | brandIcon; as brandSvg) { <mat-icon class="ntl__icon" [svgIcon]="brandSvg"></mat-icon> } @else { <mat-icon class="ntl__icon">{{ entry.row.presentation.icon }}</mat-icon> }
                </span>

                <span class="ntl__body">
                  <span class="ntl__top">
                    <span class="ntl__title" [innerHTML]="entry.row.notification.title"></span>
                    <span class="ntl__time" [attr.title]="entry.row.absoluteTime">{{ entry.row.relativeTime }}</span>
                  </span>
                  <span class="ntl__text" [innerHTML]="entry.row.notification.body"></span>
                  @if (entry.row.facts.length > 0) {
                    <span class="ntl__facts">
                      @for (fact of entry.row.facts; track fact.label ?? '' + fact.value) {
                        <span class="ntl__fact">
                          @if (fact.label) {<span class="ntl__fact-label">{{ fact.label }}</span>}{{ fact.value }}
                        </span>
                      }
                    </span>
                  }
                </span>
              </button>
              @if (entry.row.linkable) {
                <!--
                  The ONLY thing in the row that navigates, so it is a sibling
                  of the row button rather than a span inside it — a real
                  control, reachable by keyboard, and not a nested <button>.

                  Always visible. It used to fade in on hover, which was fine
                  while it was a hint about what the row would do; as the sole
                  way to reach the record, a control you cannot see until you
                  hover is a control most people never find. Hover has no
                  meaning on touch at all.
                -->
                <button
                  type="button"
                  class="ntl__open"
                  [class.ntl__open--stacked]="entry.stacked"
                  (click)="onOpen(entry.row.notification, $event)"
                  [attr.aria-label]="'Open ' + entry.row.presentation.label.toLowerCase() + ': ' + entry.row.plainTitle"
                  >
                  Open {{ entry.row.presentation.label.toLowerCase() }} →
                </button>
              }
            }
          </li>
        }
      </ul>
    }
  }
</div>
